import {
  Stack,
  Group,
  Button,
  TextInput,
  Loader,
  Alert,
  ActionIcon,
  Text,
  Select,
  SegmentedControl,
  Box,
  Modal,
} from '@mantine/core';
import { IconSearch, IconFilter } from '@tabler/icons-react';
import { useJobs, useDeleteJob, useUpdateJob, Job } from '../../api/jobs';
import { useUIStore } from '../../store/ui.store';
import JobCard from './JobCard';
import AddJobModal from './AddJobModal';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useMediaQuery } from '@mantine/hooks';

interface JobListProps {
  onJobSelect?: (job: Job) => void;
}

export default function JobList({ onJobSelect }: JobListProps) {
  const { data: jobs, isLoading, error } = useJobs();
  const { jobFilterStatus, searchQuery, setJobFilterStatus, setSearchQuery } = useUIStore();
  const deleteJobMutation = useDeleteJob();
  const updateJobMutation = useUpdateJob();
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Delete Modal State
  const [jobToDelete, setJobToDelete] = useState<Job | null>(null);

  const isMobile = useMediaQuery('(max-width: 768px)');


  const handleJobClick = (job: Job) => {
    onJobSelect?.(job);
  };

  const handleDeleteRequest = (job: Job) => {
    setJobToDelete(job);
  };

  const handleConfirmDelete = async () => {
    if (jobToDelete) {
      await deleteJobMutation.mutateAsync(jobToDelete.id);
      setJobToDelete(null);
    }
  };

  const handleArchiveInstead = async () => {
    if (jobToDelete) {
      await updateJobMutation.mutateAsync({
        jobId: jobToDelete.id,
        updates: { status: 'archived' }
      });
      setJobToDelete(null);
    }
  };

  if (isLoading) return <Loader />;
  if (error) return <Alert color="red">Error loading jobs: {error.message}</Alert>;

  let filtered: Job[] = jobs || [];

  // Apply status filter
  if (jobFilterStatus !== 'all') {
    filtered = filtered.filter((job) => job.status === jobFilterStatus);
  }

  // Apply search filter
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter(
      (job) =>
        job.title.toLowerCase().includes(query) ||
        job.company.toLowerCase().includes(query) ||
        job.url.toLowerCase().includes(query)
    );
  }

  return (
    <Stack gap="md">
      {/* Header Controls */}
      <Box>
        <Group gap="md" align="flex-start" wrap={isMobile ? 'wrap' : 'nowrap'}>
          <TextInput
            placeholder="Search jobs, companies, or keywords..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{ flex: isMobile ? '1 1 100%' : 1, width: isMobile ? '100%' : 'auto' }}
          />

          <Group gap="sm" wrap="wrap" style={{ flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
            <SegmentedControl
              value={jobFilterStatus}
              onChange={(val) => setJobFilterStatus(val as any)}
              data={[
                { label: 'All', value: 'all' },
                { label: 'Active', value: 'active' },
                { label: 'Applied', value: 'applied' },
                { label: 'Rejected', value: 'rejected' },
                // Conditionally show extra options if expanded
                ...(filtersExpanded ? [
                  { label: 'Expired', value: 'expired' },
                  { label: 'Archived', value: 'archived' },
                  { label: 'Withdrawn', value: 'withdrawn' }
                ] : [])
              ]}
            />

            <ActionIcon
              variant="light"
              size="lg"
              radius="md"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              title={filtersExpanded ? "Show fewer filters" : "Show more filters"}
            >
              <IconFilter size={20} />
            </ActionIcon>
          </Group>
        </Group>
      </Box>

      {/* Jobs List */}
      {filtered.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          {searchQuery ? 'No jobs match your search' : 'No jobs found'}
        </Text>
      ) : (
        <Stack gap="md">
          {filtered.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onDelete={() => handleDeleteRequest(job)}
              onClick={() => handleJobClick(job)}
            />
          ))}
        </Stack>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        opened={!!jobToDelete}
        onClose={() => setJobToDelete(null)}
        title="Delete Job"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete <strong>{jobToDelete?.title}</strong> at <strong>{jobToDelete?.company}</strong>?
          </Text>
          <Alert color="red" icon={<IconFilter size={16} />}>
            This action cannot be undone. All associated resumes and data will be permanently removed.
          </Alert>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setJobToDelete(null)}>
              Cancel
            </Button>
            {jobToDelete?.status !== 'archived' && (
              <Button variant="light" color="blue" onClick={handleArchiveInstead}>
                Archive Instead
              </Button>
            )}
            <Button color="red" onClick={handleConfirmDelete}>
              Delete Permanently
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

